import { camelize } from 'casing'
import { AxiosError, AxiosResponse } from 'axios'
import { get, isEmpty } from 'lodash'
import {
  getFlyoutNewOnboardingFormData,
  getFlyOutOnboardingFormData,
  putFlyoutNewOnboardingFormData,
  putOnboardingFormData,
} from '../../api/onboarding'
import { updateAppAlert } from '../../modules/appAlert/actions'
import { startLoading, stopLoading } from '../../modules/loading/actions'
import { LoadingTypes } from '../../modules/loading/reducer'
import { PartnerType } from '../../types/partner'
import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import { API } from '../../api'
import { identityServiceUrl } from '../../utils/constants'
import { getErrorMessages } from '../../utils/error'
import { errorLogger } from '../../utils/errorLogger'
import { RequestFailureMessage } from '../../utils/messagesContants'
import {
  applyDefaultCloudSettings,
  buildLockMetadataForCurrentUser,
  convertApiResponseToReducer,
  convertMicrosoftFormToApiPayload,
  convertReducerToMicrosoftForm,
  isChangeInOnboardingData,
  mapCloudCurrenciesToCurrencies,
  normalizeOnboardingApiDetails,
  processFlyoutOnboarding,
  setInitialPartnerData,
  shouldAcquireOnboardingLock,
} from './flyoutOnboardingHelpers'
import {
  getFlyoutListingData,
  getFlyoutListingDataMicrosoft,
} from './sellerListingActions'

const SET_FLYOUT_ONBOARDING_FORM = 'SET_FLYOUT_ONBOARDING_FORM'
const UPDATE_MICROSOFT_ONBOARDING_FORM = 'UPDATE_MICROSOFT_ONBOARDING_FORM'
const LABRA_PARTNER_DETAILS = 'labraPartnerDetails'

const flyOutOnboardingCompletedSuccessfully =
  'Your onboarding is successfully completed. You can now get started with creating your first listing from the Product listings page.'
const changesSavedSuccessfully = 'Changes saved successfully.'

// The app store is wider than the fields these thunks read. A narrow state
// type makes the thunks unusable with that store.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OnboardingDispatch = (action: any) => any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OnboardingGetState = () => any

const setOnboardingFormData = (onboardingFormData: Record<string, unknown>) => ({
  type: SET_FLYOUT_ONBOARDING_FORM,
  payload: onboardingFormData,
})

const updateMicrosoftOnboardingState = (microsoftOnboardingForm: unknown) => ({
  type: UPDATE_MICROSOFT_ONBOARDING_FORM,
  payload: microsoftOnboardingForm,
})

const PARTNER_IQ_FEATURE = 'PARTNERIQ'
const PARTNER_IQ_ADMIN_FEATURE = 'PARTNER_IQ'

const normalizeSalesFeatureKey = (key: string): string => {
  const upper = key.trim().toUpperCase()
  if (upper === 'PARTNER_IQ' || upper === 'PARTNERIQ') {
    return PARTNER_IQ_FEATURE
  }
  return key
}

const normalizeSalesFeatures = (features: string[] = []): string[] =>
  Array.from(new Set(features.map(normalizeSalesFeatureKey)))

const hasPartnerIqFeature = (features: string[] = []): boolean =>
  normalizeSalesFeatures(features).includes(PARTNER_IQ_FEATURE)

type SalesSubscription = {
  features?: string[]
  partnerIqEnabled?: boolean
  partnerIqOnboardingComplete?: boolean
  [key: string]: unknown
}

const normalizeSalesSubscription = (
  sales?: SalesSubscription
): SalesSubscription | undefined => {
  if (!sales) return sales

  const normalizedFeatures = normalizeSalesFeatures(sales.features ?? [])
  const partnerIqFromFeatures = hasPartnerIqFeature(normalizedFeatures)
  const partnerIqEnabled =
    sales.partnerIqEnabled === false
      ? false
      : sales.partnerIqEnabled === true || partnerIqFromFeatures

  const featuresForUi = Array.from(
    new Set(
      normalizedFeatures.map(feature =>
        feature === PARTNER_IQ_FEATURE ? PARTNER_IQ_ADMIN_FEATURE : feature
      )
    )
  )

  return {
    ...sales,
    features: featuresForUi,
    partnerIqEnabled,
    partnerIqOnboardingComplete:
      sales.partnerIqOnboardingComplete === true ? true : undefined,
  }
}

const normalizeSubscribedProducts = <
  T extends { SALES?: SalesSubscription; [key: string]: unknown },
>(
  subscribedProducts?: T
): T | undefined => {
  if (!subscribedProducts?.SALES) return subscribedProducts
  return {
    ...subscribedProducts,
    SALES: normalizeSalesSubscription(subscribedProducts.SALES),
  }
}

const setAccount = (data: Record<string, unknown>) => {
  const camelized = camelize(data) as Record<string, unknown> & {
    subscribedProducts?: { SALES?: SalesSubscription; [key: string]: unknown }
  }
  return {
    type: 'SET_PARTNER',
    payload: {
      ...camelized,
      subscribedProducts: normalizeSubscribedProducts(
        camelized.subscribedProducts
      ),
    },
  }
}

const updateAccount =
  (
    partnerId: string,
    partnerObjectType: PartnerType,
    dataPartner: Record<string, unknown>,
    infoData: Record<string, string>
  ) =>
  async (dispatch: OnboardingDispatch, getState: OnboardingGetState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    const partnerData =
      getState().AccountData[partnerObjectType]?.partnerData || {}
    try {
      const res = await API.patch(
        `${identityServiceUrl}/partners/${partnerId}`,
        dataPartner
      )
      await dispatch(
        actionTypeWrapper(
          partnerObjectType,
          setAccount({
            ...partnerData,
            ...res.data,
            partnerOwner: infoData,
          })
        )
      )
    } catch (err) {
      dispatch(
        updateAppAlert({
          message: RequestFailureMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      errorLogger({ globalState: getState() })(err as Error)
      dispatch(stopLoading(LoadingTypes.GENERAL))
      return err
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

const processPartnerDetailsData = async (
  partnerId: string,
  onboardingData: any,
  dispatch: OnboardingDispatch
) => {
  const sectionDetails = onboardingData[LABRA_PARTNER_DETAILS]
  const payload: Record<string, unknown> = {
    companyName: sectionDetails.companyName,
    companyIndustry: sectionDetails.industry,
    companyWebsite: sectionDetails.companyWebsite,
  }
  await dispatch(updateAccount(partnerId || '', PartnerType.User, payload, {}))
}

export const sendFlyOutOnboardingData =
  ({
    partnerId,
    tenantPartnerId,
    isAutoSave,
    onboardingForm,
    pageMarkerMapping,
    page,
    status,
    isOnboarded,
    callBackFlyoutOnboarding,
  }: {
    partnerId: string
    tenantPartnerId: string
    isAutoSave: boolean
    onboardingForm: any
    pageMarkerMapping: Record<string, string>
    page: string
    status?: string
    isOnboarded?: boolean
    callBackFlyoutOnboarding?: () => void
  }) =>
  async (dispatch: OnboardingDispatch, getState: OnboardingGetState) => {
    try {
      !isAutoSave &&
        (await dispatch(startLoading(LoadingTypes.SUBMIT_FLYOUT_ONBOARDING)))
      const getOnboardingStateData =
        getState()?.flyOutOnboarding?.onboardingForm
      const isFormUpdated = isChangeInOnboardingData(
        onboardingForm,
        getOnboardingStateData
      )
      const onboardingProcessedData = processFlyoutOnboarding({
        isAutoSave,
        onboardingForm,
        page,
        pageMarkerMapping,
        status,
        isFormUpdated,
        partnerId,
      })
      const onboardingPayload = JSON.parse(
        JSON.stringify(onboardingProcessedData).replaceAll('\\"', '\\\\\\"')
      )

      if (page === LABRA_PARTNER_DETAILS && !isAutoSave) {
        await processPartnerDetailsData(
          tenantPartnerId,
          onboardingForm,
          dispatch
        )
      }
      const response = await putOnboardingFormData({
        accountId: partnerId,
        data: onboardingPayload,
      })
      if (response) {
        const camelizedOnboardingData = JSON.parse(
          JSON.stringify(camelize(get(response, 'data') || {})).replaceAll(
            '\\\\"',
            '"'
          )
        )

        if (
          camelizedOnboardingData.details?.cloudSettings?.cloudCurrencies !==
          undefined
        ) {
          mapCloudCurrenciesToCurrencies(
            camelizedOnboardingData.details.cloudSettings
          )
        }

        const updatedOnboardingJson = {
          ...camelizedOnboardingData.details,
          lockMetadata: {
            ...camelizedOnboardingData.lockMetadata,
          },
        }
        if (!isAutoSave && updatedOnboardingJson.completed?.value) {
          await dispatch(
            updateAppAlert({
              message: isOnboarded
                ? 'Changes saved successfully.'
                : flyOutOnboardingCompletedSuccessfully,
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
        }

        await dispatch(setOnboardingFormData(updatedOnboardingJson))

        if (status === 'lockAcquire' && callBackFlyoutOnboarding) {
          callBackFlyoutOnboarding()
        }

        if (updatedOnboardingJson.completed?.value) {
          setTimeout(async () => {
            await dispatch(getFlyoutListingData({ partnerId, metadata: true }))
          }, 1000)
        }
      }
    } catch (error: any) {
      if (!isAutoSave) {
        const e = error as AxiosError
        const errorResponse = e?.response as AxiosResponse<ErrorResponse>

        dispatch(
          updateAppAlert({
            message: getErrorMessages([RequestFailureMessage])(errorResponse),
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        errorLogger({ globalState: getState() })(
          error as Error,
          errorResponse?.data?.track_id
        )
      } else {
        return error
      }
    } finally {
      !isAutoSave &&
        (await dispatch(stopLoading(LoadingTypes.SUBMIT_FLYOUT_ONBOARDING)))
    }
  }

export const sendMarketplaceOnboardingData =
  (
    accountId: string,
    onboardingFormData: any,
    cloudType: string,
    shouldShowSuccessMessage?: boolean
  ) =>
  async (dispatch: OnboardingDispatch, getState: OnboardingGetState) => {
    await dispatch(startLoading(LoadingTypes.SEND_FLYOUT_ONBOARDING_DATA))

    try {
      const formToApiPayload = convertMicrosoftFormToApiPayload(
        accountId,
        onboardingFormData
      )
      const response = await putFlyoutNewOnboardingFormData({
        accountId,
        data: formToApiPayload,
        cloudType,
      })
      if (response) {
        const microsoftFormData = convertApiResponseToReducer(response.data)
        if (microsoftFormData?.submit) {
          await dispatch(
            updateAppAlert({
              message: flyOutOnboardingCompletedSuccessfully,
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
        }
        await dispatch(updateMicrosoftOnboardingState(microsoftFormData))
        if (shouldShowSuccessMessage) {
          await dispatch(
            updateAppAlert({
              message: changesSavedSuccessfully,
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
        }
      }
      if (response && (response.data as { submit?: boolean }).submit) {
        setTimeout(() => {
          dispatch(getFlyoutListingDataMicrosoft({ accountId }) as any)
        }, 1000)
      }
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>

      if (errorResponse && errorResponse.status === 409) {
        return
      }

      await dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )

      errorLogger({ globalState: getState() })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.SEND_FLYOUT_ONBOARDING_DATA))
    }
  }

export const getFlyOutOnboardingData =
  (
    partnerId: string,
    tenantPartnerId: string,
    _cloudType: string,
    isOnboarded?: boolean
  ) =>
  async (dispatch: OnboardingDispatch, getState: OnboardingGetState) => {
    dispatch(startLoading(LoadingTypes.FLYOUT_ONBOARDING_DATA))
    try {
      const { data } = await getFlyOutOnboardingFormData(partnerId)
      const camelizedData = get(camelize(data), 'onboardingData') || {}
      if (isEmpty(camelizedData)) {
        const partnerData = getState().AccountData[PartnerType.User]
          .partnerData
        const initialFlyoutOnboardingFormData = applyDefaultCloudSettings(
          setInitialPartnerData(partnerData || {}),
          partnerId
        )
        await dispatch(
          setOnboardingFormData({
            ...camelize(initialFlyoutOnboardingFormData),
          })
        )
      } else {
        const updatedOnboardingData = JSON.parse(
          JSON.stringify(camelizedData).replaceAll('\\\\"', '"')
        )

        const details = updatedOnboardingData.details || {}
        normalizeOnboardingApiDetails(details)

        const updatedOnboardingJson = {
          ...details,
          lockMetadata: {
            ...updatedOnboardingData.lockMetadata,
          },
        }

        const user = getState().userProfile
        const pageMarkerMapping: Record<string, string> = {
          labraPartnerDetails: 'page1',
          cloudSettings: 'page2',
        }
        if (!isOnboarded) {
          if (
            shouldAcquireOnboardingLock(
              updatedOnboardingJson?.lockMetadata?.lastActivityAt
            )
          ) {
            updatedOnboardingJson.lockMetadata =
              buildLockMetadataForCurrentUser(
                updatedOnboardingJson.lockMetadata,
                user
              )
            await dispatch(
              sendFlyOutOnboardingData({
                partnerId,
                tenantPartnerId,
                isAutoSave: true,
                onboardingForm: updatedOnboardingJson,
                pageMarkerMapping,
                page: LABRA_PARTNER_DETAILS,
                status: 'lockAcquire',
              })
            )
          } else {
            await dispatch(setOnboardingFormData({ ...updatedOnboardingJson }))
          }
        } else {
          await dispatch(setOnboardingFormData({ ...updatedOnboardingJson }))
        }
      }
    } catch (error) {
      errorLogger({ globalState: getState() })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.FLYOUT_ONBOARDING_DATA))
    }
  }

export const getFlyoutMicrosoftOnboardingFormData =
  (accountId: string, cloudType: string, shouldSendData?: boolean) =>
  async (dispatch: OnboardingDispatch, getState: OnboardingGetState) => {
    await dispatch(startLoading(LoadingTypes.GET_FLYOUT_ONBOARDING_DATA))
    try {
      const { data } = await getFlyoutNewOnboardingFormData(
        accountId,
        cloudType
      )
      const microsoftData = convertApiResponseToReducer(data)

      if (microsoftData.lockMetadata) {
        const user = getState().userProfile

        if (
          shouldAcquireOnboardingLock(microsoftData.lockMetadata.lastActivityAt)
        ) {
          microsoftData.lockMetadata = buildLockMetadataForCurrentUser(
            microsoftData.lockMetadata,
            user
          )
          if (shouldSendData) {
            await dispatch(
              sendMarketplaceOnboardingData(
                accountId,
                convertReducerToMicrosoftForm(microsoftData),
                cloudType
              )
            )
          }
        }
      }

      await dispatch(updateMicrosoftOnboardingState(microsoftData))
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            error as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: false,
        })
      )
      errorLogger({ globalState: getState() })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_FLYOUT_ONBOARDING_DATA))
    }
  }

export { convertReducerToMicrosoftForm }
